import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchSuiteCounts as fetchSuiteCountsApi,
  getAssetsBySuiteId,
  deleteUnitArea as deleteUnitAreaApi,
} from 'src/services/client';
import { saveAssetsDone } from 'src/store/actions/assets';
import { fetchCountsDone } from 'src/store/actions/properties';
import { toast } from 'react-toastify';

interface UseUnitAreasOptions {
  suiteId: string;
  userId: string;
  roleName: string;
}

const useUnitAreas = ({ suiteId, userId, roleName }: UseUnitAreasOptions) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /* FIXME: LEGACY BOS REDUX SPAGHETTI BRIDGE
   * The AssetForm UI is hard-coded to watch the global 'validation' slice for a "Success" string.
   * Until the AssetForm is refactored to use local callbacks/promises, we must continue
   * using legacy Redux thunks (saveAsset, updateAsset) in the UI layer to trigger that 'Success' signal. */
  const retrieveSuiteCounts = useCallback(async () => {
    try {
      const response: any = await fetchSuiteCountsApi(suiteId, roleName);
      if (response.ok || response.status === 200) {
        const json = await response.json();
        if (json.statusCode === 200) {
          /* TODO: Technical Debt: Other legacy pages depend on the global 'selectPropertyCounts' selector.
           * Lift this state into a context or shared cache (React Query) later. */
          dispatch(fetchCountsDone(json.data));
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch suite counts:', err?.message);
    }
  }, [suiteId, roleName, dispatch]);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await getAssetsBySuiteId(suiteId, userId, roleName);
      if (response.ok || response.status === 200) {
        const json = await response.json();
        /* TODO: Technical Debt saveAssetsDone populates the global 'selectAssets' used by AssetForm and AreaAssetsTable.
         * Refactor to use local state once the AssetForm is decoupled from Redux. */
        dispatch(saveAssetsDone(json.data));
      } else {
        setError('Failed to fetch assets.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch assets.');
    } finally {
      setLoading(false);
    }
  }, [suiteId, userId, roleName, dispatch]);

  const deleteUnitArea = useCallback(
    async (unitAreaId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const response: any = await deleteUnitAreaApi(unitAreaId);

        if (response.ok || response.status === 200) {
          /* FIXME: FRAGILE STATE WATERFALL
           * We are following a "Happy Path" only.
           * 1. DELETE successful on Elham's new endpoint.
           * 2. Manual trigger of fetchAssets to refresh global Redux store.
           * 3. Manual trigger of retrieveSuiteCounts for UI sync.
           * * If step 2 or 3 fails, the UI will desync. This is a side effect of
           * the legacy BOS Redux architecture not being atomic. */
          await fetchAssets();
          await retrieveSuiteCounts();

          toast.success(' ⓘ Room has been deleted.', {
            autoClose: 4000,
            position: 'top-center',
          });

          return true;
        } else {
          const errorJson = await response.json().catch(() => null);
          const message =
            errorJson?.message ||
            `Failed to delete room. (Status ${response.status})`;
          setError(message);
          return false;
        }
      } catch (err: any) {
        setError(
          err?.message || 'Something went wrong while deleting the room.'
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchAssets, retrieveSuiteCounts]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    loading,
    error,
    fetchAssets,
    retrieveSuiteCounts,
    deleteUnitArea,
    clearError,
  };
};

export default useUnitAreas;
